import { ANALYTICS_NAVIGATION_MAP, DashboardTiles } from '@common/constants';
import { TilesProps } from '@common/types';
import { MenuItem } from '@cloudclevr/components';
import {
  useAddDashboardTile,
  useDashboardUpdateTileParams,
  useHandleTileBtnNavigate,
  useRemoveDashboardTile,
} from '@hooks/dashboard';
import {
  useCallsMadeSnapshotQuery,
  useEmailsSentSnapshotQuery,
  useMeetingsSentSnapshotQuery,
  useMessagesSentSnapshotQuery,
} from '@hooks/productivity';
import { UseActivitiesQueryOptions } from '@hooks/productivity/types';
import { NumberSnapshotResponse } from '@common/types';
import { NumberTile } from '@pages/InsightsAndAnalytics/tiles/NumberTile';
import { useState, useMemo, useCallback } from 'react';
import { UseQueryResult } from '@tanstack/react-query';
import { pickSummary, useDualProductivityData } from '@hooks/summary';
import { GroupedSummaryDto } from '@hooks/summary/types';
import {
  toCallsMadeSnapshot,
  toEmailsSentSnapshot,
  toMessagesSentSnapshot,
  toMeetingsSnapshot,
} from '@hooks/summary/adapters';

type ServiceActivitiesTilesConf = Record<
  number,
  {
    title: string;
    btnLabel: string;
    fn: QueryFunction<NumberSnapshotResponse>;
    mapSummary: (grouped: GroupedSummaryDto | undefined) => NumberSnapshotResponse | undefined;
  }
>;

type ServiceActivitiesHelperProps = TilesProps & {
  type: number;
};

type Params = {
  days: string;
};

type QueryFunction<T> = (
  options: UseActivitiesQueryOptions
) => UseQueryResult<T>;

export const ServiceActivitiesHelper: React.FC<
  ServiceActivitiesHelperProps
> = ({ params, id, type, classNames }) => {
  const [parameters, setParameters] = useState<Params>(JSON.parse(params));
  const { handleButtonClick } = useHandleTileBtnNavigate(id);
  const useDashboardUpdateTileParamsMutation = useDashboardUpdateTileParams();
  const { removeMenuItem } = useRemoveDashboardTile(id);
  const { addMenuItem } = useAddDashboardTile(type, '');
  const isOnDashboardPage = !!id;

  // Memoize mapSummary functions based on parameters.days
  const mapEmailsSent = useCallback(
    (grouped: GroupedSummaryDto | undefined) =>
      toEmailsSentSnapshot(pickSummary(grouped, 'UserActivity'), parameters.days),
    [parameters.days]
  );

  const mapMessagesSent = useCallback(
    (grouped: GroupedSummaryDto | undefined) =>
      toMessagesSentSnapshot(pickSummary(grouped, 'UserActivity'), parameters.days),
    [parameters.days]
  );

  const mapCallsMade = useCallback(
    (grouped: GroupedSummaryDto | undefined) =>
      toCallsMadeSnapshot(pickSummary(grouped, 'UserActivity'), parameters.days),
    [parameters.days]
  );

  const mapMeetings = useCallback(
    (grouped: GroupedSummaryDto | undefined) =>
      toMeetingsSnapshot(pickSummary(grouped, 'UserActivity'), parameters.days),
    [parameters.days]
  );

  const tilesConfig: ServiceActivitiesTilesConf = useMemo(
    () => ({
      [DashboardTiles.EmailsSent]: {
        title: 'Emails sent',
        btnLabel: 'Emails data',
        fn: useEmailsSentSnapshotQuery,
        mapSummary: mapEmailsSent,
      },
      [DashboardTiles.MessagesSent]: {
        title: 'Messages sent',
        btnLabel: 'Messages data',
        fn: useMessagesSentSnapshotQuery,
        mapSummary: mapMessagesSent,
      },
      [DashboardTiles.CallsMade]: {
        title: 'Calls made',
        btnLabel: 'Calls data',
        fn: useCallsMadeSnapshotQuery,
        mapSummary: mapCallsMade,
      },
      [DashboardTiles.MeetingsSent]: {
        title: 'Meetings joined',
        btnLabel: 'Meetings data',
        fn: useMeetingsSentSnapshotQuery,
        mapSummary: mapMeetings,
      },
    }),
    [mapEmailsSent, mapMessagesSent, mapCallsMade, mapMeetings]
  );

  const {
    data: serviceActivityData,
    isLoading: isServiceActivityDataLoading,
    refetch,
  } = useDualProductivityData({
    legacyParams: parameters,
    useLegacyQuery: tilesConfig[type].fn,
    mapSummary: tilesConfig[type].mapSummary,
  });

  const handleServiceAdoptionDropdown = (value: string) => {
    const newParameters = {
      days: value,
    };

    setParameters(newParameters);

    if (isOnDashboardPage) {
      useDashboardUpdateTileParamsMutation.mutate({
        params: JSON.stringify(newParameters),
        tileId: id,
      });

      refetch();
    }
  };

  const createDropdownMenuItems = (): MenuItem[] => [
    {
      id: '1',
      label: 'Last 7 days',
      value: '7',
      action: () => handleServiceAdoptionDropdown('7'),
    },
    {
      id: '2',
      label: 'Last 30 days',
      value: '30',
      action: () => handleServiceAdoptionDropdown('30'),
    },
    {
      id: '3',
      label: 'Last 60 days',
      value: '60',
      action: () => handleServiceAdoptionDropdown('60'),
    },
    {
      id: '4',
      label: 'Last 90 days',
      value: '90',
      action: () => handleServiceAdoptionDropdown('90'),
    },
  ];

  return (
    <NumberTile
      dropdownMenuItemClasses={'!w-[300px]'}
      sortData={createDropdownMenuItems()}
      initialSelectedItem={createDropdownMenuItems().find(
        (item) => item.value === parameters.days
      )}
      classNames={classNames ?? 'col-span-6 md:col-span-3 xl:col-span-2'}
      percentageLabel=""
      contextData={isOnDashboardPage ? removeMenuItem : addMenuItem}
      headerTitle={tilesConfig[type].title}
      isLoading={isServiceActivityDataLoading}
      data={serviceActivityData ? Object.values(serviceActivityData) : []}
      buttonLabel={tilesConfig[type].btnLabel}
      linkPath={ANALYTICS_NAVIGATION_MAP['activityData']}
      handleButtonClick={handleButtonClick()}
    />
  );
};
